import {
  useCreateWordMatMutation,
  useUpdateWordMatMutation,
} from '@/app/redux/endpoints/wordMats';
import { useAppSelector } from '@/app/redux/hooks';
import { useCurrentOrganisationId } from '@/app/redux/hooks/organisation';
import { useWordMats } from '@/app/redux/hooks/wordMats';
import { selectUserId } from '@/app/redux/slices/sessionSlice';
import { WordMatItem, WordMatState } from '@/common/types/wordMat';
import EditorHeader from '@/common/ui/EditorHeader';
import { UNKNOWN_ERROR_MESSAGE } from '@/common/ui/UnknownErrorMessage';
import { NotFoundError } from '@/common/utils/error';
import trackMixpanelEvent from '@/common/utils/mixpanel';
import { useAutosave } from '@/common/utils/useAutosave';
import { PresentationIcon } from '@phosphor-icons/react';
import { Button } from '@shadcn/components/button';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import WordMat from './WordMat';
import WordMatPresentMode from './WordMatPresentMode';

// Helper to remove undefined values from items (Firebase doesn't accept undefined)
function cleanWordMatState(state: WordMatState): WordMatState {
  const cleanedCategories: Record<string, WordMatItem[]> = {};

  for (const [categoryName, items] of Object.entries(state.customCategories)) {
    cleanedCategories[categoryName] = items.map((item) => {
      const cleanItem: WordMatItem = {
        id: item.id,
        text: item.text,
      };
      if (item.color !== undefined) {
        cleanItem.color = item.color;
      }
      return cleanItem;
    });
  }

  return {
    ...state,
    customCategories: cleanedCategories,
  };
}

export default function WordMatView() {
  const navigate = useNavigate();

  const { id } = useParams<{ id: string }>();
  const isNew = id === 'new';
  const isDefault = id === 'default';

  const [searchParams] = useSearchParams();
  const [draftData] = useState<WordMatState | null>(() => {
    if (searchParams.get('from') !== 'public') return null;
    const raw = localStorage.getItem('wordMatDraft');
    if (!raw) return null;
    return JSON.parse(raw) as WordMatState;
  });

  const { currentOrganisationId } = useCurrentOrganisationId();
  const userId = useAppSelector(selectUserId);

  const {
    wordMatsById,
    isLoading: matsLoading,
    isFetching: matsFetching,
  } = useWordMats();
  // Skip DB lookup for default (view-only, no DB record) and 'new'
  const wordMat =
    !isDefault && id && id !== 'new' ? wordMatsById.get(id) : undefined;

  const [createWordMat] = useCreateWordMatMutation();
  const [updateWordMat] = useUpdateWordMatMutation();

  const [name, setName] = useState(
    isDefault ? 'Spellcaster Word Mat' : wordMat?.name || 'Untitled Word Mat',
  );
  const [currentState, setCurrentState] = useState<WordMatState | null>(null);
  const [isPresentOpen, setIsPresentOpen] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const [lastSaved, setLastSaved] = useState(wordMat?.updatedAt);
  const hasSavedFromPublicRef = useRef(false);

  const { isSaving } = useAutosave({
    saveFn: handleSave,
    trigger: [currentState, name],
    disabled: !isReady,
  });

  // Sync name when word mat data becomes available (e.g. direct URL navigation)
  useEffect(() => {
    if (wordMat && !isReady) {
      setName(wordMat.name);
    }
  }, [wordMat]); // eslint-disable-line react-hooks/exhaustive-deps

  const initialData = useMemo(() => {
    if (wordMat) {
      return {
        paletteMode: wordMat.paletteMode,
        language: wordMat.language,
        visibleCategories: wordMat.visibleCategories,
        customCategories: wordMat.customCategories,
        customCategoryColors: wordMat.customCategoryColors,
        tileSize: wordMat.tileSize,
        interactionMode: wordMat.interactionMode,
        showColors: wordMat.showColors,
        alphabetColorCode: wordMat.alphabetColorCode,
        tileSwapEnabled: wordMat.tileSwapEnabled,
      };
    }
    // Default template — use WordMat's built-in defaults
    if (isDefault) return undefined;
    // Restore draft from standalone editor if coming from public route
    if (draftData) return draftData;
    // Blank new word mat starts in custom mode
    return { paletteMode: 'custom' as const };
  }, [wordMat, isDefault, draftData]);

  // Auto-save immediately when landing from public page
  useEffect(() => {
    if (!draftData || !isNew || !isReady || !userId || !currentOrganisationId) return;
    if (hasSavedFromPublicRef.current) return;
    hasSavedFromPublicRef.current = true;
    handleSave();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftData, isNew, isReady, userId, currentOrganisationId]);

  const handleStateChange = useCallback((state: WordMatState) => {
    setCurrentState(state);
    setIsReady(true);
  }, []);

  async function handleSave() {
    if (!currentState || !currentOrganisationId) {
      console.error('Cannot save: missing required fields');
      toast.error('Failed to save word mat', {
        description: UNKNOWN_ERROR_MESSAGE,
      });
      return;
    }

    const cleanedState = cleanWordMatState(currentState);

    try {
      if (isNew || isDefault) {
        if (!userId) {
          toast.error('Failed to save word mat', {
            description: UNKNOWN_ERROR_MESSAGE,
          });
          return;
        }
        const newWordMat = await createWordMat({
          createdBy: userId,
          organisationId: currentOrganisationId,
          name,
          ...cleanedState,
        }).unwrap();
        trackMixpanelEvent('Word Mat Created', {
          wordMatId: newWordMat.id,
          wordMatName: name,
          paletteMode: cleanedState.paletteMode,
        });
        localStorage.removeItem('wordMatDraft');
        navigate(`/dashboard/wordmats/${newWordMat.id}`, { replace: true });
      } else {
        await updateWordMat({
          orgId: currentOrganisationId,
          id: id!,
          updates: { name, ...cleanedState },
        }).unwrap();
      }
    } catch (error) {
      console.error('Error saving word mat:', error);
      toast.error('Failed to save word mat', {
        description: UNKNOWN_ERROR_MESSAGE,
      });
    } finally {
      setLastSaved(Date.now());
    }
  }

  // Gate rendering while loading an existing word mat
  if (!isDefault && !isNew && matsLoading && !wordMat) return null;

  if (!isDefault && !isNew && !matsLoading && !matsFetching && !wordMat) {
    throw new NotFoundError('Word mat not found');
  }

  return (
    <>
      <div className="flex flex-col h-full">
        <EditorHeader
          name={name}
          onNameChange={setName}
          isSaving={isSaving}
          lastSaved={lastSaved}
          backPath="/dashboard/wordmats"
          saveDisabledReason={
            isDefault
              ? 'This word mat is view only, make a copy to edit'
              : undefined
          }
          actions={
            <Button
              variant="outline"
              onClick={() => setIsPresentOpen(true)}
              className="flex gap-2"
            >
              <PresentationIcon size={18} />
              Present
            </Button>
          }
        />
        <div className="flex-1 overflow-auto">
          <WordMat
            initialData={initialData}
            onStateChange={handleStateChange}
          />
        </div>
      </div>
      {isPresentOpen && currentState && (
        <WordMatPresentMode
          initialData={currentState}
          onClose={() => setIsPresentOpen(false)}
        />
      )}
    </>
  );
}
